import ActionListStatusGroup from '@/components/ActionListCompletedActions';
import ActionRow from '@/components/ActionRow';
import AddActionButton from '@/components/AddActionButton';
import BlockDragOverlay from '@/components/BlockDragOverlay';
import Callout from '@/components/Callout';
import CaptureList from '@/components/CaptureList';
import CategoryDragOverlay from '@/components/CategoryDragOverlay';
import CategorySelector from '@/components/CategorySelector';
import ConfirmDeleteBlockModal from '@/components/ConfirmDeleteBlockModal';
import CreateBlockModal from '@/components/CreateBlockModal';
import DeleteBlockModal from '@/components/DeleteBlockModal';
import DeleteBulkActionsModal, { modeValues } from '@/components/DeleteBulkActionsModal/DeleteBulkActionModal';
import DndContainer from '@/components/DndContainer';
import { ActionList, DroppableContainer } from '@/components/DragAndDrop';
import ActionListItem from '@/components/DragAndDrop/ActionListItem';
import DnDBlockItem from '@/components/DragAndDrop/DnDBlockItem';
import { DragAndDropCalendarWrapper } from '@/components/DragAndDropCalendar';
import CalendarProvider from '@/components/DragAndDropCalendar/provider';
import DuplicateBlockModal from '@/components/DuplicateBlockModal';
import DynamicSegmentedButton from '@/components/DynamicSegmentedButton';
import EditBlockModal from '@/components/EditBlockModal';
import EmptyActionList from '@/components/EmptyActionList';
import EmptyCategories from '@/components/EmptyCategories';
import EmptyCategoryCallout from '@/components/EmptyCategoryCallout';
import ScheduleEventModal, { ScheduleEventDateTime } from '@/components/MyPlanStep/ScheduleEventModal';
import Timeline from '@/components/MyPlanStep/Timeline';
import EventItem from '@/components/MyPlanStep/WeeklyCalendar/WeeklyEventItem';
import RecurringInBlockModal from '@/components/RecurringEventModal/RecurringInBlockModal';
import RecurringEventModal, { EventOptions } from '@/components/RecurringEventModal/RecurringEventModal';
import ThreeDotsLaterButton from '@/components/ThreeDotsLaterButton';
import UncompletedActionsModal from '@/components/UncompletedActionsModal';
import { ALL_ACTIONS, COMPLETE, INCOMPLETE, UNSCHEDULED_ACTIONS, ValueLabelType } from '@/constants/action';
import { DAY, WEEK } from '@/constants/calendar';
import getPlannerMotionVariants from '@/constants/plannerMotionVariants';
import { RecurrenceRule } from '@/gql/events/types';
import { keys } from '@/gql/global/keys';
import useCaptureListData from '@/hooks/useCaptureListData';
import DashboardLayout from '@/layouts/Dashboard';
import { sortBlocksByCompletion } from '@/pages/WeeklyPlan/MyWeeklyPlan/helpers';
import { ActionTimeChangeProps } from '@/pages/WeeklyPlan/Schedule';
import { useCreateAction, useUpdateAction } from '@/services/action/hooks';
import { useBlocks } from '@/services/block/hooks';
import {
  useBlockEvents,
  useCreateBlockEvent,
  useDeleteBlockEvent,
  useUpdateBlockEvent,
} from '@/services/blockEvent/hooks';
import { useCategories } from '@/services/categories/hooks';
import {
  useCategoryEvents,
  useCreateCategoryEvent,
  useDeleteCategoryEvent,
  useUpdateCategoryEvent,
} from '@/services/categoryEvent/hooks';
import { useCreateEvent as useEvent, useUpdateEventByPk } from '@/services/events/hooks';
import {
  completeActionsOfBlock,
  onMutateBlockStatus,
  refetchBlocks,
  removeIncompleteActionsFromBlock,
  useActionsBlocksAndEvents,
  useAddActionToBlock,
  useCompleteBlockStatus,
  useCreateEvent,
  useDeleteBlock,
  useDeleteEventAndUpdateAction,
  useDeleteEventAndUpdateBlockAction,
  useIncompleteBlockStatus,
  useRemoveActionFromBlock,
  useUpdateActionsCategoryOrder,
  useUpdateBlockActionsBlockOrder,
} from '@/services/myPlan/hooks';
import useIsDailyPlanPage from '@/services/plans/hooks/useIsDailyPlanPage';
import { useWeeklyPlanId } from '@/services/plans/hooks/useWeeklyPlan';
import { useActionModal } from '@/stores/useActionModal';
import { useCalendarMonthlyStore } from '@/stores/useCalendar';
import useToggleExpandCalendar, { DailyPlanView } from '@/stores/useToggleExpandCalendar';
import { IconCategories, IconPlus } from '@/theme/icons';
import { Action, ProgressStatus, RecurringWeekContext, isPlannedAction } from '@/types/actions';
import { Block } from '@/types/block';
import { MyEvent } from '@/types/calendar';
import { ActionEventType, CategoryActionType } from '@/types/myPlan';
import {
  filterIncompleteActions,
  getActionById,
  getActionsByBlockId,
  getActionsByCategoryId,
  getIncompleteActions,
  getIncompleteActionsByBlockId,
  getTotalDurationOfActions,
  getTotalDurationOfActionsInRange,
  getTotalDurationOfPreferredActions,
  getTotalDurationOfPreferredActionsInRange,
  refetchActions,
} from '@/utils/action';
import { getBlockById } from '@/utils/block';
import {
  computeRecurrenceExceptionDates,
  endOfTheWeek,
  formatDateToString,
  getDaysWithinDatesRange,
  getGMTOffset,
  localDateToUTC,
  normalizeRecurrenceRuleUntil,
  startOfTheWeek,
  utcToLocalDate,
} from '@/utils/calendar';
import { actionHasRecurrenceForbiddenInBlock } from '@/utils/blockRecurringPolicy';
import { findContainerById, getIdx, getItemById } from '@/utils/dnd';
import { getValueInRecurrenceRule, validateRecurrence } from '@/utils/events';
import { isUncategorized } from '@/utils/index';
import {
  ItemType,
  createActionEvent,
  formatActions,
  getEventByActionId,
  getEventById,
  groupActionsByBlockId,
  groupEventsByWeekDay,
} from '@/utils/myplan';
import rem from '@/utils/rem';
import { invalidateQueries } from '@/utils/tanStackQuery';
import {
  trackActionScheduledByDrag,
  trackBlockTimeScheduled,
  trackBulkActionCompleted,
  trackCategoryTimeScheduled,
} from '@/utils/tracking';
import { useMapBy } from '@/utils/useMapBy';
import { Accordion, Flex, Icon, Spinner, Text, useBoolean, useDisclosure } from '@chakra-ui/react';
import {
  CollisionDetection,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DropAnimation,
  UniqueIdentifier,
  closestCenter,
  defaultDropAnimationSideEffects,
  getFirstCollision,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { endOfDay, format, isBefore, startOfDay } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { isEmpty, isNull, isUndefined, orderBy } from 'lodash';
import { CSSProperties, Fragment, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
};

function Schedule() {
  const { t } = useTranslation('DailyPlan');
  const [selectedSegmented, setSelectedSegmented] = useState(ALL_ACTIONS);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeItemCameFrom, setActiveItemCameFrom] = useState('');
  const [tempBlockActionId, setTempBlockActionId] = useState<string | null>(null);
  const [tempCategoryId, setTempCategoryId] = useState<string | null>(null);
  const [tempProjectId, setTempProjectId] = useState<string | null>(null);
  const [selectedBlock, setSelectedBlock] = useState<Block | null>(null);
  const [reScheduleBlock, setReScheduleBlock] = useState<boolean>(false);
  const [eventToUnplan, setEventToUnplan] = useState<MyEvent | null>(null);
  const [newTimeChanged, setNewTimeChanged] = useState<ActionTimeChangeProps>();
  const [combinedEvents, setCombinedEvents] = useState<MyEvent[]>();
  const currentEvent = useRef<MyEvent | null>(null);
  const [selectedCategoryForBulk, setSelectedCategoryForBulk] = useState<any | null>(null);
  const [showCanceledModal, setShowCanceledModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const lastOverId = useRef<UniqueIdentifier | null>(null);
  const recentlyMovedToNewContainer = useRef(false);

  const actionsContainerRef = useRef<HTMLDivElement | null>(null);

  const isDailyPlanPage = useIsDailyPlanPage();
  const { CAPTURE_LIST_VARIANTS } = getPlannerMotionVariants(isDailyPlanPage);

  const { isCalendarExpanded, dailyPlanView } = useToggleExpandCalendar();

  const { formattedBlocks, categories, notEmptyCategories, filterDailyScheduledActions } =
    useCaptureListData(selectedSegmented);

  const [isEditRecurringEvent, setIsEditRecurringEvent] = useBoolean();
  const [isDeleteRecurringEvents, setIsDeleteRecurringEvents] = useBoolean();

  const handleCloseDeleteDuplicatedAction = useCallback(() => {
    setEventToUnplan(null);
    setIsDeleteRecurringEvents.off();
    setIsEditRecurringEvent.off();
  }, [setIsDeleteRecurringEvents]);

  const handleCloseEditRecurringAction = useCallback(() => {
    setEventToUnplan(null);
    setIsEditRecurringEvent.off();
    setNewTimeChanged(undefined);
  }, [setIsEditRecurringEvent]);

  const {
    isOpen: isOpenCreateBlockModal,
    onOpen: openCreateBlockModal,
    onClose: onCloseCreateBlockModal,
  } = useDisclosure();

  const { isOpen: isOpenEditBlockModal, onOpen: openEditBlockModal, onClose: onCloseEditBlockModal } = useDisclosure();
  const {
    isOpen: isOpenDuplicateBlockModal,
    onOpen: openDuplicatedBlockModal,
    onClose: onCloseDuplicatedBlockModal,
  } = useDisclosure();

  const {
    isOpen: isOpenSetUncompletedActionsModal,
    onOpen: openSetUncompletedActionsModal,
    onClose: onCloseSetUncompletedActionsModal,
  } = useDisclosure();

  const {
    isOpen: isOpenDeleteBlockModal,
    onOpen: openDeleteBlockModal,
    onClose: onCloseDeleteBlockModal,
  } = useDisclosure();

  const {
    isOpen: isOpenConfirmDeleteBlockModal,
    onOpen: openConfirmDeleteBlockModal,
    onClose: onCloseConfirmDeleteBlockModal,
  } = useDisclosure();

  const {
    isOpen: isScheduleEventModalOpen,
    onOpen: onOpenScheduleEventModal,
    onClose: onCloseScheduleEventModal,
  } = useDisclosure();

  const {
    isOpen: isOpenRecurringInBlockModal,
    onOpen: openRecurringInBlockModal,
    onClose: closeRecurringInBlockModal,
  } = useDisclosure();

  const { data: categoriesData } = useCategories();
  const categoriesMap = useMapBy(categoriesData?.category ?? [], 'id');

  const { data: blocksData } = useBlocks();
  const blocksMap = useMapBy(blocksData?.block ?? [], 'id');

  const { onGetCurrentActionData: getCurrentActionData, setActionModalOpen } = useActionModal();

  const selectedDate = useCalendarMonthlyStore((state) => state.selectedDate);

  const dailyPlanRecurringContext = useMemo((): RecurringWeekContext | null => {
    if (!isDailyPlanPage) {
      return null;
    }
    return {
      weekStart: startOfDay(selectedDate),
      weekEnd: endOfDay(selectedDate),
      granularity: 'day',
    };
  }, [isDailyPlanPage, selectedDate]);

  const startDate = selectedDate && startOfTheWeek(selectedDate);
  const endDate = selectedDate && endOfTheWeek(selectedDate);
  const weekDays = getDaysWithinDatesRange(startDate, endDate);

  const captureDurationRange = useMemo(
    () => (selectedDate ? { start: startOfDay(selectedDate), end: endOfDay(selectedDate) } : null),
    [selectedDate],
  );

  const { data: weeklyPlanId } = useWeeklyPlanId(selectedDate);
  const { data: categoryEventsData } = useCategoryEvents(startDate, endDate);
  const { data: blockEventsData } = useBlockEvents(startDate, endDate);

  const { data, isLoading } = useActionsBlocksAndEvents();

  const items = useMemo<ItemType>(() => {
    if (!data) {
      return {
        captureList: [],
      };
    }

    const activeActions = data.action.filter((a) => !a.isCanceled);
    let captureList = isDailyPlanPage
      ? filterDailyScheduledActions(formatActions(activeActions))
      : formatActions(activeActions);

    const categories = categoriesData?.category?.map((category) => {
      const actionsEvents = getActionsByCategoryId(data.action, category.id).filter(isPlannedAction);

      const events: CategoryActionType[] = actionsEvents.map((action) => ({
        event: {
          id: String(action?.event?.id),
          action: { ...action, typeName: 'Action' },
          actionId: action.id,
          typeName: 'Event',
        },
      }));

      return {
        ...category,
        actions: events,
      };
    });

    return {
      ...groupEventsByWeekDay(categories ?? [], weekDays),
      ...groupActionsByBlockId(data.block, data.action),
      captureList,
    };
  }, [data, categoriesData?.category, weekDays, filterDailyScheduledActions, isDailyPlanPage]);

  const scheduleListSegmentedButtonOptions: ValueLabelType[] = useMemo(
    () => [
      { value: ALL_ACTIONS, label: t('segmentedButtons.allActions') },
      { value: UNSCHEDULED_ACTIONS, label: t('segmentedButtons.unscheduled') },
    ],
    [t],
  );

  const updateActionMutation = useUpdateAction({
    onSuccess: () => {
      setIsEditRecurringEvent.off();
      currentEvent.current = null;
    },
  });

  const createAction = useCreateAction();

  const updateCategoryEvent = useUpdateCategoryEvent();

  const updateBlockEvent = useUpdateBlockEvent();

  const addActionToBlock = useAddActionToBlock();

  const removeActionFromBlock = useRemoveActionFromBlock();

  const updateActionsCategoryOrder = useUpdateActionsCategoryOrder();

  const updateBlockActionsBlockOrder = useUpdateBlockActionsBlockOrder();

  const createEvent = useCreateEvent();

  const createEventOne = useEvent();

  const updateEvent = useUpdateEventByPk({
    onSuccess: () => {
      setIsDeleteRecurringEvents.off();
      setIsEditRecurringEvent.off();
      setNewTimeChanged(undefined);
      currentEvent.current = null;
    },
  });

  const createCategoryEvent = useCreateCategoryEvent();

  const createBlockEvent = useCreateBlockEvent();

  const deleteEventAndUpdateAction = useDeleteEventAndUpdateAction({
    onSuccess: () => {
      setIsDeleteRecurringEvents.off();
      currentEvent.current = null;
    },
  });

  const deleteEventAndUpdateBlockAction = useDeleteEventAndUpdateBlockAction();

  const deleteBlock = useDeleteBlock();

  const deleteCategoryEvent = useDeleteCategoryEvent();

  const deleteBlockEvent = useDeleteBlockEvent();

  const completeBlockStatus = useCompleteBlockStatus({
    onMutate: ({ id }) => onMutateBlockStatus(id, true),
  });

  const incompleteBlockStatus = useIncompleteBlockStatus({
    onMutate: ({ id }) => onMutateBlockStatus(id),
  });

  /* <Dnd> */
  const collisionDetectionStrategy = useCallback<CollisionDetection>(
    (args) => {
      if (activeId && activeId in items) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((container) => container.id in items),
        });
      }

      // Start by finding any intersecting droppable
      const pointerIntersections = pointerWithin(args);
      const intersections =
        pointerIntersections.length > 0
          ? // If there are droppables intersecting with the pointer, return those
            pointerIntersections
          : rectIntersection(args);

      let overId = getFirstCollision(intersections, 'id');

      // We have to ensure that overId is not null or undefined
      if (overId != null) {
        if (overId in items && !overId.toString().startsWith('event')) {
          const containerItems = items[overId];

          // If a container is matched and it contains items (columns 'A', 'B', 'C')
          if (containerItems.length > 0) {
            // Return the closest droppable within that container
            const tempOverId = closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                (container) =>
                  container.id !== overId && containerItems.find((item) => item.action.id === container.id),
              ),
            })[0]?.id;

            if (!isUndefined(tempOverId)) {
              overId = tempOverId;
            }
          }
        }

        lastOverId.current = overId;

        return [{ id: overId }];
      }

      // When a draggable item moves to a new container, the layout may shift
      // and the `overId` may become `null`. We manually set the cached `lastOverId`
      // to the id of the draggable item that was moved to the new container, otherwise
      // the previous `overId` will be returned which can cause items to incorrectly shift positions
      if (recentlyMovedToNewContainer.current) {
        lastOverId.current = activeId;
      }

      // If no droppable is matched, return the last match
      return lastOverId.current ? [{ id: lastOverId.current }] : [];
    },
    [activeId, items],
  );

  const onDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id);
    setActiveItemCameFrom(active?.data?.current?.containerId);
  };

  const updateEventDate = useCallback(
    (actionId: string, scheduledDate: string, scheduledTime: string | null, timeZone?: string, gmtOffset?: string) => {
      updateActionMutation.mutate(
        {
          actionId,
          set: {
            scheduledDate,
            scheduledTime,
            timezone: timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
            gmtOffset: gmtOffset ?? getGMTOffset(timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone),
          },
        },
        {
          onSuccess: (actionData) => {
            const recurrence = actionData.updateActionByPk.event?.recurrence;
            if (recurrence) {
              const eventId = actionData.updateActionByPk.event?.id;
              const newStart = actionData?.updateActionByPk?.scheduledDate || '';
              updateEvent.mutate({
                id: eventId as string,
                recurrence: { ...recurrence },
                start: newStart,
              });
            }
          },
        },
      );
    },
    [updateActionMutation],
  );

  const createEventIfNotExistsAndUpdateAction = useCallback(
    (
      actionId: string,
      scheduledDate: string,
      scheduledTime: string | null = null,
      timeZone?: string,
      gmtOffset?: string,
    ) => {
      if (!data?.action) {
        return;
      }

      if (!getEventByActionId(data.action, actionId)) {
        createEvent.mutate({
          id: uuidv4(),
          actionId,
          scheduledDate,
          scheduledTime,
          timezone: timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
          gmtOffset: gmtOffset ?? getGMTOffset(timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone),
        });

        return;
      }

      const event = getEventByActionId(data.action, actionId);

      if (!event) {
        return;
      }

      updateEventDate(
        actionId,
        scheduledDate,
        scheduledTime ?? event.action.scheduledTime,
        timeZone ?? event.action.timezone,
        gmtOffset ?? event.action.gmtOffset,
      );
    },
    [data?.action, updateEventDate, createEvent],
  );

  const moveActions = (reorderItems: ActionEventType[]) => {
    return reorderItems.map((action, index) => ({
      ...action,
      action: {
        ...action.action,
        categoryOrder: index + 1,
      },
    }));
  };

  const updateCaptureListActionsOrder = useCallback(
    (actions: ActionEventType[]) => {
      const mutationPayload = actions.map((action, index) => ({
        _set: {
          categoryOrder: index + 1,
        },
        where: {
          id: {
            _eq: action.action.id,
          },
        },
      }));

      updateActionsCategoryOrder.mutate(mutationPayload);
    },
    [updateActionsCategoryOrder],
  );

  const updateBlockActionsOrder = useCallback(
    (actions: Action[]) => {
      const mutationPayload = actions.map((action, index) => ({
        _set: {
          blockOrder: index + 1,
        },
        where: {
          id: {
            _eq: action.id,
          },
        },
      }));

      updateBlockActionsBlockOrder.mutate({
        updates: mutationPayload,
        categoryId: actions[0].categoryId,
        projectId: actions[0].projectId,
      });
    },
    [updateBlockActionsBlockOrder],
  );

  const handleDeleteEventAndUpdateAction = useCallback(
    async (eventId: string, actions: ActionEventType[], blockId: string | null) => {
      const event = getEventById(data.action, eventId);

      if (event == null) {
        return;
      }

      if (!event.action.scheduledDate) {
        return;
      }

      let shouldDeleteBlock = false;

      if (blockId) {
        const block = data.block.find((block) => block.id === blockId);
        const blockActions = block?.actions;

        const blockUpdatedAction = blockActions?.find((action) => action.id === event.actionId);

        if (blockUpdatedAction != null) {
          actions.push(createActionEvent({ ...blockUpdatedAction, typeName: 'Action' }));
        }

        if (blockActions?.length === 1) {
          shouldDeleteBlock = true;
        }
      }

      let categoryOrder = 0;

      if (data.action.length) {
        categoryOrder = data.action.length;
      }

      await deleteEventAndUpdateAction.mutateAsync({
        eventId,
        actionId: event.actionId,
        scheduledDate: isDailyPlanPage
          ? formatDateToString(
              utcToLocalDate(
                event.action.scheduledDate,
                event.action.scheduledTime,
                event.action.timezone,
                event.action.gmtOffset,
              ),
            )
          : null,
        categoryOrder,
      });

      if (shouldDeleteBlock && blockId) {
        deleteBlock.mutate({
          blockId,
        });
      }
    },
    [data.action, data.block, deleteEventAndUpdateAction, deleteBlock, isDailyPlanPage],
  );

  const handleAddActionToBlock = useCallback(
    (actionId: string, block: Block) => {
      if (!data?.block) {
        return;
      }

      const draggedAction = getActionById(data.action, actionId);
      if (actionHasRecurrenceForbiddenInBlock(draggedAction)) {
        openRecurringInBlockModal();
        return;
      }

      addActionToBlock.mutate({
        actionId,
        blockId: block.id,
        blockOrder: data?.block?.length ?? 0,
        projectId: block.projectId,
        categoryId: block.categoryId,
      });

      incompleteBlockStatus.mutate({ id: block.id });

      const cacheKeys = [];
      cacheKeys.push(keys.blocks.all.queryKey);
      block.projectId && cacheKeys.push(keys.project.detail(block.projectId).queryKey);
      invalidateQueries(cacheKeys);

      onCloseCreateBlockModal();
      setTempBlockActionId(null);
    },
    [data.block, data.action, addActionToBlock, incompleteBlockStatus, onCloseCreateBlockModal, openRecurringInBlockModal],
  );

  const moveActionFromBlockToCaptureList = useCallback(
    (actionId: string, blockId: string, shouldDeleteBlock: boolean) => {
      if (!data) {
        return;
      }

      const block = getBlockById(data.block, blockId);

      if (shouldDeleteBlock && block) {
        setSelectedBlock(block);
        openConfirmDeleteBlockModal();
        return;
      }

      removeActionFromBlock.mutate({
        actionId,
        categoryId: block?.categoryId,
        projectId: block?.projectId,
      });
    },
    [data, openConfirmDeleteBlockModal, removeActionFromBlock],
  );

  const handleDeleteEventAndUpdateBlockAction = useCallback(
    (actions: ActionEventType[], eventId: string, blockId: string) => {
      const event = getEventById(data.action, eventId);

      if (event == null) {
        return;
      }

      const actionForEvent = getActionById(data.action, event.actionId);
      if (actionHasRecurrenceForbiddenInBlock(actionForEvent)) {
        openRecurringInBlockModal();
        return;
      }

      deleteEventAndUpdateBlockAction.mutate({
        eventId,
        actionId: event.actionId,
        scheduledDate: isDailyPlanPage ? event.action.scheduledDate : null,
        blockId,
        blockOrder: event.action.blockOrder,
      });
    },
    [data.action, deleteEventAndUpdateBlockAction, isDailyPlanPage, openRecurringInBlockModal],
  );

  const handleDailyActionDropped = useCallback(
    async (dateTime: ScheduleEventDateTime) => {
      if (!activeId) {
        return;
      }

      const actionId = String(activeId);
      const { scheduledDate, scheduledTime, timeZone } = dateTime;
      const gmtOffset = getGMTOffset(timeZone);
      const action = getActionById(data.action, actionId);

      if (!action) {
        return;
      }

      if (!action.scheduledDate) {
        await updateActionMutation.mutateAsync({
          actionId,
          set: {
            scheduledDate,
            scheduledTime,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            gmtOffset: gmtOffset ?? getGMTOffset(Intl.DateTimeFormat().resolvedOptions().timeZone),
          },
        });
      }

      createEventIfNotExistsAndUpdateAction(
        actionId,
        scheduledDate,
        scheduledTime,
        timeZone,
        gmtOffset ?? getGMTOffset(Intl.DateTimeFormat().resolvedOptions().timeZone),
      );
      trackActionScheduledByDrag({ ...action, scheduledDate });
      onCloseScheduleEventModal();
    },
    [activeId, createEventIfNotExistsAndUpdateAction, data.action, onCloseScheduleEventModal, updateActionMutation],
  );

  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      const activeContainer = findContainerById(active.id, items);

      if (!activeContainer || active.id == null) {
        setActiveId(null);
        setActiveItemCameFrom('');
        return;
      }

      let overId = over?.id;
      if (over?.data?.current?.containerId === 'blocks') {
        // special case when an item is dropped over a block but not over the list of actions
        overId = `block-${overId}`;
      }

      if (overId == null) {
        setActiveId(null);
        setActiveItemCameFrom('');
        return;
      }

      if (overId === 'timeline') {
        setActiveId(active.id);
        onOpenScheduleEventModal();
        return;
      }

      const overContainer = findContainerById(overId, items);

      if (overContainer) {
        let activeIndex = items[activeContainer].findIndex(({ id }) => id === active?.id);
        let overIndex = items[overContainer].findIndex(({ id }) => id === over?.id);

        const overContainerId = over?.data?.current?.containerId;

        if (
          active?.data?.current?.category?.id !== over?.data?.current?.category?.id &&
          over?.data?.current?.category?.id !== 'captureListContainer'
        ) {
          return;
        }

        const activeProjectId = active?.data?.current?.projectId;
        const overProjectId = over?.data?.current?.projectId;

        if (
          overContainerId.toString().startsWith('block-') &&
          ![null, undefined].includes(activeProjectId, overProjectId) &&
          activeProjectId !== overProjectId
        ) {
          return;
        }

        if (activeItemCameFrom === 'captureList' && overContainerId.startsWith('event')) {
          createEventIfNotExistsAndUpdateAction(active.id.toString(), over?.data?.current?.day);
        }

        if (activeItemCameFrom.startsWith('event') && overContainerId.startsWith('event')) {
          const event = getEventById(data.action, active.id.toString());

          if (!event) {
            return;
          }

          if (over?.data?.current?.day) {
            updateEventDate(event.actionId, over.data.current.day, null);
          }
        }

        if (activeItemCameFrom === 'captureList' && overContainerId === 'captureList') {
          updateCaptureListActionsOrder(moveActions(arrayMove(items[overContainer], activeIndex, overIndex)));
        }

        if (activeItemCameFrom.startsWith('event') && overContainerId === 'captureList') {
          let blockId = getEventById(data.action, active.id.toString())?.action?.blockId ?? null;

          let actions: ActionEventType[] = [];

          if (items[overContainer].length) {
            actions = moveActions(arrayMove(items[overContainer], activeIndex, overIndex));
          }

          handleDeleteEventAndUpdateAction(active.id.toString(), actions, blockId);
        }

        if (activeItemCameFrom.startsWith('block') && overContainerId.startsWith('event')) {
          createEventIfNotExistsAndUpdateAction(active.id.toString(), over?.data?.current?.day);
        }

        if (activeItemCameFrom === 'captureList' && overContainerId.toString().startsWith('block')) {
          const block = getBlockById(data?.block ?? [], over?.data?.current?.blockId);

          if (block) {
            handleAddActionToBlock(active.id.toString(), block);
          }
        }

        if (activeItemCameFrom.startsWith('block') && overContainerId === 'captureList') {
          moveActionFromBlockToCaptureList(
            active.id.toString(),
            active?.data?.current?.blockId,
            items[activeContainer].length === 1,
          );
        }

        if (
          activeContainer.toString().startsWith('block') &&
          overContainerId.startsWith('block') &&
          activeContainer.toString() !== overContainerId
        ) {
          const block = getBlockById(data?.block ?? [], over?.data?.current?.blockId);

          if (block) {
            handleAddActionToBlock(active.id.toString(), block);
          }
        }

        if (
          activeContainer.toString().startsWith('block') &&
          overContainerId.startsWith('block') &&
          activeContainer.toString() === overContainerId
        ) {
          const blockIncompleteActions = orderBy(
            getIncompleteActionsByBlockId(active?.data?.current?.blockId, data.action),
            'blockOrder',
            'asc',
          );

          activeIndex = blockIncompleteActions.findIndex(({ id }) => id === active?.id);
          overIndex = blockIncompleteActions.findIndex(({ id }) => id === over?.id);

          updateBlockActionsOrder(arrayMove(blockIncompleteActions, activeIndex, overIndex));
        }

        if (activeItemCameFrom.startsWith('event') && overContainerId.startsWith('block')) {
          let actions: ActionEventType[] = [];

          if (items[overContainer].length) {
            actions = moveActions(arrayMove(items[overContainer], activeIndex, overIndex));
          }

          handleDeleteEventAndUpdateBlockAction(actions, active.id.toString(), over?.data?.current?.blockId);
        }
      }

      setActiveId(null);
      setActiveItemCameFrom('');
    },
    [
      items,
      activeItemCameFrom,
      createEventIfNotExistsAndUpdateAction,
      data.action,
      data?.block,
      updateEventDate,
      updateCaptureListActionsOrder,
      handleDeleteEventAndUpdateAction,
      handleAddActionToBlock,
      moveActionFromBlockToCaptureList,
      updateBlockActionsOrder,
      handleDeleteEventAndUpdateBlockAction,
      onOpenScheduleEventModal,
    ],
  );

  const handleTimeChange = useCallback(
    async (event: MyEvent, time: Date) => {
      const { scheduledDate, scheduledTime, gmtOffset } = localDateToUTC(time);

      if (event.__metadata?.action) {
        if (validateRecurrence(event.__metadata.action.event?.recurrence)) {
          currentEvent.current = event;
          setIsEditRecurringEvent.on();
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          setNewTimeChanged({
            scheduledDate: scheduledDate,
            scheduledTime: scheduledTime,
            gmtOffset: gmtOffset,
            timeZone: timezone,
          });
        } else {
          await updateActionMutation.mutateAsync({
            actionId: event.__metadata?.action.id,
            set: {
              scheduledDate,
              scheduledTime,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              gmtOffset,
            },
          });
        }
      } else if (event.__metadata?.categoryEvent) {
        updateCategoryEvent.mutate({
          id: event.__metadata?.categoryEvent.id,
          scheduledDate,
          scheduledTime,
          duration: event.__metadata?.categoryEvent.duration,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          gmtOffset,
        });
      } else if (event.__metadata?.blockEvent) {
        updateBlockEvent.mutate({
          id: event.__metadata?.blockEvent.id,
          scheduledDate,
          scheduledTime,
          duration: event.__metadata?.blockEvent.duration,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          gmtOffset,
        });
      }
    },
    [updateActionMutation, updateCategoryEvent, updateBlockEvent, setIsEditRecurringEvent],
  );

  const handleRemoveActionFromBlock = useCallback(
    (actionId: string, blockId: string) => {
      const key = `block-${blockId}`;

      moveActionFromBlockToCaptureList(actionId, blockId, items[key].length === 1);
    },
    [items, moveActionFromBlockToCaptureList],
  );

  const handleClickEvent = useCallback(
    (event: MyEvent) => {
      const action = event.__metadata?.action;
      if (!action) return;

      if (action?.blockId) {
        const block = getBlockById(formattedBlocks, action.blockId);

        action.block = block ?? null;
        action.isLastBlockAction = block?.actions?.length === 1;
      }

      getCurrentActionData(action);
      setActionModalOpen(true, {
        readonlyCategory: true,
        readonlyTooltipLabel: t('tooltips.readonlyCategory'),
      });
    },
    [formattedBlocks, getCurrentActionData, setActionModalOpen, t],
  );

  const onDragCancel = () => {
    setActiveId(null);
    setActiveItemCameFrom('');
  };

  const ItemOverlay = useMemo(() => {
    if (!activeId) {
      return <></>;
    }

    let overlayElement: ReactNode = null;
    let dragOverlayStyle: CSSProperties | undefined;

    const draggedBlock = data.block?.find((b) => b.id === String(activeId));
    const draggedCategory = categories?.find((c) => c.id === String(activeId));

    if (draggedBlock) {
      const blockActions = getActionsByBlockId(draggedBlock.id, data.action);
      overlayElement = <BlockDragOverlay block={{ ...draggedBlock, actions: blockActions }} />;
      dragOverlayStyle = { maxWidth: rem(350) };
    } else if (draggedCategory) {
      overlayElement = <CategoryDragOverlay category={draggedCategory} />;
      dragOverlayStyle = { width: 'unset' };
    } else {
      const item = getItemById(activeId, items);

      if (!item) {
        return <></>;
      }

      const index = getIdx(activeId, items);

      overlayElement = (
        <>
          {item?.typeName?.toLowerCase() === 'event' ? (
            <EventItem event={item} onUnplan={() => null} onClickEvent={() => null} />
          ) : (
            <ActionRow
              action={item.action}
              idx={index + 1}
              recurringWeekContext={dailyPlanRecurringContext ?? undefined}
              disableRecurringStatusChanges={validateRecurrence(item.action?.event?.recurrence)}
            />
          )}
        </>
      );
    }

    return createPortal(
      <DragOverlay dropAnimation={dropAnimation} style={dragOverlayStyle}>
        {overlayElement}
      </DragOverlay>,
      document.body,
    );
  }, [activeId, categories, items, data.action, data.block, dailyPlanRecurringContext]);

  /* </Dnd> */

  const onUpdateBlock = useCallback(() => {
    refetchBlocks();

    onCloseEditBlockModal();
    setSelectedBlock(null);
  }, [onCloseEditBlockModal]);

  const handleDeleteBlock = useCallback(
    (block: Block | undefined) => {
      if (!block) {
        return;
      }

      setSelectedBlock(block);
      openDeleteBlockModal();
    },
    [openDeleteBlockModal],
  );

  const onCompleteBlockActions = useCallback(() => {
    if (!selectedBlock) {
      return;
    }

    if (selectedBlock.actions) {
      const incompleteActions = getIncompleteActions(selectedBlock.actions);
      incompleteActions.forEach((action) => trackBulkActionCompleted(action));
    }

    completeActionsOfBlock(selectedBlock.categoryId);

    setSelectedBlock(null);
  }, [selectedBlock]);

  const onCompleteBlock = useCallback(
    (blockId: string) => {
      completeBlockStatus.mutate({
        id: blockId,
      });
    },
    [completeBlockStatus],
  );

  const onRemoveBlockActions = useCallback(() => {
    if (!selectedBlock) {
      return;
    }

    removeIncompleteActionsFromBlock(selectedBlock);
    onCompleteBlock(selectedBlock.id);

    setSelectedBlock(null);
  }, [onCompleteBlock, selectedBlock]);

  const onUncompleteBlock = useCallback(
    (blockId: string) => {
      if (!data) {
        return;
      }

      const block = getBlockById(data.block, blockId);

      if (!block) {
        return;
      }

      incompleteBlockStatus.mutate({
        id: blockId,
      });
    },
    [data, incompleteBlockStatus],
  );

  const createOrUpdateAction = async (
    action: Action,
    newTimeChanged: ActionTimeChangeProps,
    frequency?: RecurrenceRule,
  ) => {
    const actionData = {
      blockId: action?.blockId ?? undefined,
      categoryId: action?.categoryId ?? undefined,
      dateOfStarring: action?.dateOfStarring,
      duration: newTimeChanged?.duration ?? action?.duration ?? '',
      id: uuidv4(),
      isLocked: false,
      notes: action?.notes,
      progressStatus: INCOMPLETE as ProgressStatus,
      projectId: action?.projectId ?? null,
      scheduledDate: newTimeChanged?.scheduledDate,
      scheduledTime: newTimeChanged?.scheduledTime,
      timezone: newTimeChanged?.timeZone,
      title: action?.title,
      weeklyPlanId: weeklyPlanId,
      gmtOffset: newTimeChanged?.gmtOffset,
    };

    createAction.mutate(actionData, {
      onSuccess: async (data: any) => {
        const { id: actionId } = data.insertActionOne;

        if (data.insertActionOne.scheduledDate) {
          const eventData: any = {
            id: uuidv4(),
            actionId,
          };

          if (frequency && validateRecurrence({ rules: [frequency], exceptions: {} })) {
            eventData.recurrence = {
              rules: [frequency],
              exceptions: {},
            };
            eventData.start = data.insertActionOne.scheduledDate;
            eventData.end = null;
          }

          const event = await createEventOne.mutateAsync(eventData);

          if (event?.insertEventOne) {
            data.insertActionOne.event = {
              id: event.insertEventOne.id,
              typeName: 'Event',
            };
          }
        }

        refetchActions(data.insertActionOne, weeklyPlanId);
      },
    });
  };

  const deleteRecurringEvents = (option: EventOptions | null) => {
    const action = currentEvent.current?.__metadata?.action;

    const currentExceptions = action?.event?.recurrence?.exceptions?.add || [];

    const recurrenceRule = action?.event?.recurrence?.rules?.[0] || '';
    const untilEvent = getValueInRecurrenceRule(action?.event?.recurrence, 'until') || '';

    if (option === EventOptions.THIS_EVENT) {
      const isUniqueEvent = combinedEvents?.filter((event: any) => event.eventId === action?.id).length === 1;
      if (isUniqueEvent) {
        if (action?.blockId) {
          handleDeleteEventAndUpdateBlockAction(items[action.blockId], action!.event!.id, action.blockId);
          return;
        }
        handleDeleteEventAndUpdateAction(action!.event!.id, items.captureList, null);
      } else {
        updateEvent.mutate({
          id: action?.event?.id as string,
          recurrence: {
            rules: [
              {
                ...(recurrenceRule as any),
                ...(untilEvent ? { until: untilEvent } : {}),
              },
            ],
            exceptions: {
              add: [...currentExceptions, { date: action?.scheduledDate as string }],
            },
          },
          start: action?.event?.start,
        });
      }
    }

    if (option === EventOptions.ALL_EVENTS) {
      if (!action?.event?.id) return;

      if (action.blockId) {
        handleDeleteEventAndUpdateBlockAction(items[action.blockId], action.event.id, action.blockId);
        return;
      }

      handleDeleteEventAndUpdateAction(action.event.id, items.captureList, null);
    }

    if (option === EventOptions.THIS_AND_FOLLOWING_EVENTS) {
      const isNotFirstEvent = combinedEvents?.some((event: MyEvent) =>
        isBefore(event.start, currentEvent.current!.start),
      );

      const eventEnd = action?.scheduledDate;

      if (isNotFirstEvent) {
        updateEvent.mutate({
          id: action?.event?.id as string,
          recurrence: {
            rules: [
              {
                ...(recurrenceRule as any),
                until: eventEnd as string,
              },
            ],
            exceptions: {
              add: [...currentExceptions, { date: eventEnd as string }],
            },
          },
          start: action?.event?.start,
          end: eventEnd as string,
        });
      } else {
        if (!action?.event?.id) return;
        if (action.blockId) {
          handleDeleteEventAndUpdateBlockAction(items[action.blockId], action.event.id, action.blockId);
          return;
        }

        handleDeleteEventAndUpdateAction(action.event.id, items.captureList, null);
      }
    }
  };

  const editRecurringEvents = (option: EventOptions | null) => {
    const action = currentEvent.current?.__metadata?.action;
    const currentExceptions = action?.event?.recurrence?.exceptions?.add || [];
    const frequency = action?.event?.recurrence?.rules?.[0]?.frequency || '';
    const recurrenceRules = action?.event?.recurrence?.rules?.[0] as RecurrenceRule;

    switch (option) {
      case EventOptions.THIS_EVENT: {
        if (!action) return;

        if (!newTimeChanged) return;

        const newDate = newTimeChanged.scheduledDate;
        const dateExists = currentExceptions?.some((e: any) => e.date === newDate);

        const exceptions = dateExists ? currentExceptions : [...currentExceptions, { date: newDate }];

        updateEvent.mutate({
          id: action?.event?.id as string,
          recurrence: {
            rules: [recurrenceRules],
            exceptions: {
              add: exceptions,
            },
          },
          start: action.event?.start,
        });

        createOrUpdateAction(action, newTimeChanged);

        break;
      }
      case EventOptions.THIS_AND_FOLLOWING_EVENTS: {
        if (!action) return;

        if (!newTimeChanged) return;

        const originalStartDate = action.event?.start?.split('T')[0];
        const movingToDate = newTimeChanged.scheduledDate;
        const currentEventDate = format(currentEvent.current?.start as Date, 'yyyy-MM-dd');

        const isFirstInstance = originalStartDate === currentEventDate;

        if (isFirstInstance) {
          const normalizedRule = normalizeRecurrenceRuleUntil(movingToDate, recurrenceRules, {
            timeZone: newTimeChanged?.timeZone ?? action?.timezone,
            gmtOffset: newTimeChanged?.gmtOffset ?? action?.gmtOffset,
            fallbackTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          });

          updateEvent.mutate({
            id: action?.event?.id as string,
            recurrence: {
              rules: [normalizedRule],
              exceptions: { add: currentExceptions },
            },
            start: movingToDate,
          });

          updateActionMutation.mutate({
            actionId: action.id,
            set: {
              scheduledDate: movingToDate,
              scheduledTime: newTimeChanged.scheduledTime,
              timezone: newTimeChanged.timeZone,
              gmtOffset: newTimeChanged.gmtOffset,
            },
          });
        } else {
          const newDate = format(currentEvent.current?.start as Date, 'yyyy-MM-dd');

          const { untilUtc, exceptionUtc, exceptions } = computeRecurrenceExceptionDates({
            targetDate: newDate,
            scheduledTime: action.scheduledTime,
            timeZone: action.timezone,
            gmtOffset: action.gmtOffset,
            existingExceptions: currentExceptions,
            fallbackTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          });

          updateEvent.mutate({
            id: action?.event?.id as string,
            recurrence: {
              rules: [{ ...recurrenceRules, until: untilUtc }],
              exceptions: { add: exceptions },
            },
            start: action.event?.start,
            end: exceptionUtc,
          });

          createOrUpdateAction(action, newTimeChanged, recurrenceRules);
        }

        break;
      }

      case EventOptions.ALL_EVENTS: {
        updateActionMutation.mutate({
          actionId: action?.id as string,
          set: {
            scheduledDate: action?.event?.start,
            scheduledTime: newTimeChanged?.scheduledTime,
            timezone: newTimeChanged?.timeZone,
            gmtOffset: newTimeChanged?.gmtOffset,
            ...(newTimeChanged?.duration !== undefined && { duration: newTimeChanged.duration }),
          },
        });

        break;
      }

      default:
        break;
    }
  };

  const unplanEvent = useCallback(
    (event: MyEvent) => {
      const action = event.__metadata?.action;

      if (action?.event?.id) {
        if (validateRecurrence(action.event?.recurrence)) {
          setIsDeleteRecurringEvents.on();
          currentEvent.current = event;
          return;
        }

        if (action.blockId) {
          handleDeleteEventAndUpdateBlockAction(items[action.blockId], action.event.id, action.blockId);
          return;
        }

        handleDeleteEventAndUpdateAction(action.event.id, items.captureList, null);
      }

      const categoryEvent = event.__metadata?.categoryEvent;
      if (categoryEvent?.id) {
        if (!categoryEventsData?.categoryEvent.find((ce) => ce.id === categoryEvent.id)) {
          // The event was already deleted
          return;
        }
        // TODO figure out why this setTimeout is here or remove if no issues are reported
        // setTimeout(() => deleteCategoryEvent.mutate({ id: categoryEvent.id }), 0);
        deleteCategoryEvent.mutate({ id: categoryEvent.id });
      }

      const blockEvent = event.__metadata?.blockEvent;
      if (blockEvent?.id) {
        if (!blockEventsData?.blockEvent.find((be) => be.id === blockEvent.id)) {
          // The event was already deleted
          return;
        }
        // TODO figure out why this setTimeout is here or remove if no issues are reported
        // setTimeout(() => deleteCategoryEvent.mutate({ id: categoryEvent.id }), 0);
        deleteBlockEvent.mutate({ id: blockEvent.id });
      }

      setEventToUnplan(null);
    },
    [
      categoryEventsData?.categoryEvent,
      blockEventsData?.blockEvent,
      deleteCategoryEvent,
      deleteBlockEvent,
      handleDeleteEventAndUpdateAction,
      handleDeleteEventAndUpdateBlockAction,
      items,
      setIsDeleteRecurringEvents,
    ],
  );

  const onDeleteBlock = useCallback(() => {
    if (!selectedBlock) {
      return;
    }

    deleteBlock.mutate({ blockId: selectedBlock.id });

    onCloseDeleteBlockModal();
    onCloseConfirmDeleteBlockModal();
    setSelectedBlock(null);
    onCloseEditBlockModal();
  }, [deleteBlock, onCloseConfirmDeleteBlockModal, onCloseDeleteBlockModal, onCloseEditBlockModal, selectedBlock]);

  const handleDurationChange = useCallback(
    (event: MyEvent, start: Date, duration: string) => {
      const { scheduledDate, scheduledTime } = localDateToUTC(start);
      if (event.__metadata?.action) {
        const action = event.__metadata.action;
        if (validateRecurrence(action.event?.recurrence)) {
          currentEvent.current = event;
          setIsEditRecurringEvent.on();
          setNewTimeChanged({
            scheduledDate: action.scheduledDate ?? scheduledDate,
            scheduledTime: action.scheduledTime ?? scheduledTime,
            gmtOffset: action.gmtOffset ?? '',
            timeZone: action.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
            duration,
          });
          return;
        }
        updateActionMutation.mutate({
          actionId: action.id,
          set: {
            scheduledDate,
            scheduledTime,
            duration,
          },
        });
      } else if (event.__metadata?.categoryEvent) {
        updateCategoryEvent.mutate({
          id: event.__metadata?.categoryEvent.id,
          scheduledDate,
          scheduledTime,
          duration,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          gmtOffset: getGMTOffset(Intl.DateTimeFormat().resolvedOptions().timeZone),
        });
      } else if (event.__metadata?.blockEvent) {
        updateBlockEvent.mutate({
          id: event.__metadata?.blockEvent.id,
          scheduledDate,
          scheduledTime,
          duration,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          gmtOffset: getGMTOffset(Intl.DateTimeFormat().resolvedOptions().timeZone),
        });
      }
    },
    [updateActionMutation, updateCategoryEvent, updateBlockEvent, setIsEditRecurringEvent, setNewTimeChanged],
  );

  const handleEntityDropped = useCallback(
    async (entityId: string, date: Date) => {
      // Dropping an action into daily view. Set the action info and create event
      const { scheduledDate, scheduledTime, gmtOffset } = localDateToUTC(date);

      const category = categoriesMap.get(entityId);
      if (category) {
        createCategoryEvent.mutate({
          id: uuidv4(),
          categoryId: entityId,
          scheduledDate,
          scheduledTime,
          duration: '01:00:00',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          gmtOffset,
        });

        trackCategoryTimeScheduled(isDailyPlanPage ? DAY : WEEK);

        return;
      }

      const block = blocksMap.get(entityId);
      if (block) {
        createBlockEvent.mutate({
          id: uuidv4(),
          blockId: entityId,
          scheduledDate,
          scheduledTime,
          duration: '01:00:00',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          gmtOffset,
        });

        trackBlockTimeScheduled(isDailyPlanPage ? DAY : WEEK);

        return;
      }

      const action = getActionById(data.action, entityId);

      if (!action) {
        return;
      }

      if (!action.scheduledDate) {
        await updateActionMutation.mutateAsync({
          actionId: entityId,
          set: {
            scheduledDate,
            scheduledTime,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            gmtOffset,
          },
        });
      }

      // Check if the action already has an associated event.
      createEventIfNotExistsAndUpdateAction(
        entityId,
        scheduledDate,
        scheduledTime,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        gmtOffset,
      );
      trackActionScheduledByDrag({ ...action, scheduledDate });
    },
    [
      categoriesMap,
      createCategoryEvent,
      createEventIfNotExistsAndUpdateAction,
      data.action,
      updateActionMutation,
      isDailyPlanPage,
      blocksMap,
      createBlockEvent,
    ],
  );

  const onCreateBlockFromAction = useCallback(
    (actionId: string) => {
      setTempBlockActionId(actionId);
      const tempAction = getActionById(data.action, actionId);
      setTempCategoryId(tempAction?.categoryId ?? null);
      setTempProjectId(tempAction?.projectId ?? null);
      openCreateBlockModal();
    },
    [openCreateBlockModal, data.action],
  );

  const onCloseAndResetCreateBlockModal = useCallback(() => {
    setTempBlockActionId(null);
    setTempCategoryId(null);
    setTempProjectId(null);
    onCloseCreateBlockModal();
  }, [onCloseCreateBlockModal]);

  const captureListAnimate = useMemo(() => {
    if (isCalendarExpanded) {
      return 'plannerExpanded';
    }

    return 'default';
  }, [isCalendarExpanded]);

  // I tried to remove this effect, but without it the unplan of actions
  // sometimes doesn't work, because the getEventById used inside the unplan
  // function doesn't find the action provided. Was unable to find the cause in
  // the time I had available.
  // TODO: find a way to remove this effect
  useEffect(() => {
    if (eventToUnplan === null) {
      return;
    }

    unplanEvent(eventToUnplan);

    return () => setEventToUnplan(null);
  }, [eventToUnplan, unplanEvent]);

  if (isLoading) {
    return (
      <Flex justifyContent="center" marginTop={rem(80)}>
        <Spinner size="xl" speed="0.65s" thickness={rem(4)} />
      </Flex>
    );
  }

  return (
    <DashboardLayout pageTitle={t('pageTitle')} editActionModalTooltip={t('tooltips.editActionModalTooltip')}>
      <Flex height={`calc(100vh - 9rem)`} transition="right 0.8s">
        <CategorySelector />
        <DndContainer
          collisionDetection={collisionDetectionStrategy}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}
        >
          <AnimatePresence initial={false}>
            <motion.div
              variants={CAPTURE_LIST_VARIANTS}
              animate={captureListAnimate}
              exit="normalWidth"
              transition={{ duration: 0.5 }}
              style={{ height: 'inherit' }}
            >
              <Flex
                className="capture-list-container"
                ref={actionsContainerRef}
                sx={{
                  WebkitOverflowScrolling: 'touch',
                  overscrollBehavior: 'contain',
                }}
                flexDirection="column"
                overflowY="auto"
                height="inherit"
                marginRight={rem(8)}
                paddingRight={rem(16)}
              >
                <DroppableContainer
                  id="captureList"
                  payload={{
                    containerId: 'captureList',
                    category: { id: 'captureListContainer' },
                  }}
                  height="full"
                >
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="nowrap"
                    flexDirection="row"
                    marginBottom={rem(24)}
                  >
                    <Text as="h2" textStyle="largeBlack">
                      {t('sections.captureList')}
                    </Text>
                    <DynamicSegmentedButton
                      onChange={setSelectedSegmented}
                      options={scheduleListSegmentedButtonOptions}
                      layoutId="capture-list-plan"
                    />
                  </Flex>
                  {notEmptyCategories.map((category) => (
                    <Fragment key={category.id}>
                      <CaptureList
                        className={`${category.id}-completed-actions`}
                        category={category}
                        totalDurationOfIncompleteActions={
                          captureDurationRange
                            ? getTotalDurationOfActionsInRange(
                                filterIncompleteActions(
                                  category.incompleteActions,
                                  selectedDate,
                                  'day',
                                  selectedDate,
                                ),
                                captureDurationRange.start,
                                captureDurationRange.end,
                              )
                            : getTotalDurationOfActions(
                                filterIncompleteActions(
                                  category.incompleteActions,
                                  selectedDate,
                                  'day',
                                  selectedDate,
                                ),
                              )
                        }
                        totalDurationOfPreferredIncompleteActions={
                          captureDurationRange
                            ? getTotalDurationOfPreferredActionsInRange(
                                filterIncompleteActions(
                                  category.incompleteActions,
                                  selectedDate,
                                  'day',
                                  selectedDate,
                                ),
                                captureDurationRange.start,
                                captureDurationRange.end,
                              )
                            : getTotalDurationOfPreferredActions(
                                filterIncompleteActions(
                                  category.incompleteActions,
                                  selectedDate,
                                  'day',
                                  selectedDate,
                                ),
                              )
                        }
                        marginBottom={rem(16)}
                        draggableCategory={dailyPlanView === DailyPlanView.Expand}
                        threeDotsButton={
                          !isEmpty(category.incompleteActions) && (
                            <ThreeDotsLaterButton
                              showDoItLater={false}
                              onCancel={() => {
                                setSelectedCategoryForBulk(category);
                                setShowCanceledModal(true);
                              }}
                              onDelete={() => {
                                setSelectedCategoryForBulk(category);
                                setShowDeleteModal(true);
                              }}
                            />
                          )
                        }
                        addEntityButton={
                          <AddActionButton
                            backgroundColor={category?.color}
                            withActionBlockCreationMenu={true}
                            onExternalClick={() => {
                              setActionModalOpen(true, { categoryId: category?.id, location: 'schedule' });
                            }}
                            onCreateNewBlock={() => {
                              setTempCategoryId(category.id);
                              openCreateBlockModal();
                            }}
                            color="text-primary"
                            textColor="text-primary"
                            iconSize={rem(10)}
                            minWidth={rem(24)}
                            width={rem(24)}
                            height={rem(24)}
                            marginRight={rem(8)}
                          />
                        }
                      >
                        <ActionList
                          emptyListComponent={
                            <DroppableContainer
                              id="captureList"
                              payload={{
                                containerId: 'captureList',
                                category,
                              }}
                            >
                              <EmptyActionList
                                title={
                                  selectedSegmented !== ALL_ACTIONS && !isEmpty(category.scheduledActions)
                                    ? t('emptyStates.allActionsScheduled')
                                    : t('emptyStates.allActionsCompleted')
                                }
                                motivational="small"
                              />
                            </DroppableContainer>
                          }
                          isEmpty={isEmpty(
                            filterIncompleteActions(category.incompleteActions, selectedDate, 'day', selectedDate),
                          )}
                        >
                          <SortableContext
                            items={filterIncompleteActions(
                              category.incompleteActions,
                              selectedDate,
                              'day',
                              selectedDate,
                            )}
                          >
                            {filterIncompleteActions(category.incompleteActions, selectedDate, 'day', selectedDate).map(
                              (action, index) => (
                                <motion.div
                                  key={action.id}
                                  initial="hidden"
                                  whileInView="visible"
                                  transition={{ duration: 0.5 }}
                                  viewport={{ root: actionsContainerRef }}
                                  variants={{
                                    visible: { opacity: 1 },
                                    hidden: { opacity: 0 },
                                  }}
                                >
                                  <ActionListItem
                                    item={action}
                                    index={index}
                                    onClickCreateBlock={onCreateBlockFromAction}
                                    recurringWeekContext={dailyPlanRecurringContext}
                                    disableRecurringStatusChanges={
                                      validateRecurrence(action.event?.recurrence) ? true : false
                                    }
                                    dndPayload={{
                                      containerId: 'captureList',
                                      categoryId: action.categoryId,
                                      category,
                                      projectId: action?.projectId,
                                    }}
                                  />
                                </motion.div>
                              ),
                            )}
                          </SortableContext>
                        </ActionList>
                        <ActionListStatusGroup
                          actions={category.completedActions}
                          title={t('sections.completedActions')}
                          emptyMessage={t('emptyStates.noCompletedActions')}
                          onClickCreateBlock={onCreateBlockFromAction}
                          recurringWeekContext={dailyPlanRecurringContext}
                        />
                      </CaptureList>
                      {category.blocks.sort(sortBlocksByCompletion).map((block, index) => (
                        <DroppableContainer
                          style={{ marginBottom: rem(16) }}
                          id={block.key}
                          key={block.id}
                          payload={{
                            containerId: block.key,
                            blockId: block.id,
                            category: block.category,
                            projectId: block?.projectId,
                          }}
                        >
                          <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ root: actionsContainerRef }}
                            transition={{ duration: 0.5 }}
                            variants={{
                              visible: { opacity: 1 },
                              hidden: { opacity: 0 },
                            }}
                          >
                            <DnDBlockItem
                              key={block.id}
                              actions={block?.actions ?? []}
                              block={block}
                              index={index}
                              weeklyPlanId={weeklyPlanId}
                              recurringWeekContext={dailyPlanRecurringContext}
                              onCompleteBlock={onCompleteBlock}
                              onSelectBlock={(options) => {
                                setSelectedBlock(block);
                                setReScheduleBlock(!!options?.isReschedule);
                              }}
                              openSetUncompletedActionsModal={openSetUncompletedActionsModal}
                              onUncompleteBlock={onUncompleteBlock}
                              onClickRemoveFromBlock={handleRemoveActionFromBlock}
                              onDeleteBlock={handleDeleteBlock}
                              openEditBlockModal={openEditBlockModal}
                              openDuplicateBlockModal={openDuplicatedBlockModal}
                              draggable={dailyPlanView === DailyPlanView.Expand}
                              collapsedByDefault={block.isCompleted}
                            />
                          </motion.div>
                        </DroppableContainer>
                      ))}
                    </Fragment>
                  ))}
                  {notEmptyCategories.length === 0 && <EmptyCategoryCallout />}
                  {categories &&
                    categories.length === 1 &&
                    isUncategorized(categories[0]) &&
                    categories[0].blocks.length === 0 && (
                      <Callout title={t('emptyStates.howToGetStarted.title')} backgroundColor="background-tertiary">
                        <Text textStyle="small">{t('emptyStates.howToGetStarted.description')}</Text>
                        <Text
                          sx={{
                            'span:not(.block)': {
                              opacity: '0.5',
                            },
                            '.block': {
                              display: 'inline-flex',
                              gap: rem(4),
                              verticalAlign: 'top',
                              marginRight: rem(8),
                            },
                          }}
                          textStyle="small"
                          marginTop={rem(14)}
                          color="text-secondary"
                        >
                          <span>{t('emptyStates.howToGetStarted.categoryInstructions')}</span>
                          <Icon as={IconPlus} boxSize={rem(14)} verticalAlign="top" marginX={rem(4)} />
                          <span style={{ marginRight: rem(8) }}>{t('emptyStates.howToGetStarted.categoryFilter')}</span>
                          <span className="block">
                            <Icon as={IconCategories} boxSize={rem(14)} /> {t('emptyStates.howToGetStarted.categories')}
                          </span>
                          <span>{t('emptyStates.howToGetStarted.inTheHeader')}</span>
                        </Text>
                      </Callout>
                    )}
                  <Accordion allowMultiple defaultIndex={[-1]}>
                    <EmptyCategories
                      viewportRef={actionsContainerRef}
                      openCreateBlockModal={openCreateBlockModal}
                      setActionModalOpen={setActionModalOpen}
                      setTempCategoryId={setTempCategoryId}
                    />
                  </Accordion>
                </DroppableContainer>
                {ItemOverlay}
              </Flex>
            </motion.div>
          </AnimatePresence>

          {dailyPlanView === DailyPlanView.Condense ? (
            <CalendarProvider date={selectedDate} view="day">
              <DroppableContainer id="timeline" height="full" payload={{ containerId: 'timeline' }}>
                <Timeline
                  onClickEvent={handleClickEvent}
                  onUnplan={setEventToUnplan}
                  setCombinedEvents={setCombinedEvents}
                />
              </DroppableContainer>
            </CalendarProvider>
          ) : (
            <DragAndDropCalendarWrapper
              date={selectedDate}
              height="inherit"
              view={isDailyPlanPage ? DAY : WEEK}
              onEntityDropped={handleEntityDropped}
              onTimeChange={handleTimeChange}
              onDurationChange={handleDurationChange}
              onUnplan={setEventToUnplan}
              onClickEvent={handleClickEvent}
              setCombinedEvents={setCombinedEvents}
            />
          )}
        </DndContainer>
      </Flex>

      {isOpenDuplicateBlockModal && !isNull(selectedBlock) && (
        <DuplicateBlockModal
          reScheduleBlock={reScheduleBlock}
          block={selectedBlock}
          isOpen={isOpenDuplicateBlockModal}
          onClose={onCloseDuplicatedBlockModal}
        />
      )}

      {isOpenEditBlockModal && !isNull(selectedBlock) && (
        <EditBlockModal
          isOpen
          block={selectedBlock}
          weeklyPlanId={weeklyPlanId}
          onUpdateBlock={onUpdateBlock}
          onDeleteBlock={handleDeleteBlock}
          onCancel={onCloseEditBlockModal}
        />
      )}

      {isOpenSetUncompletedActionsModal && selectedBlock && (
        <UncompletedActionsModal
          blockId={selectedBlock.id}
          completedActions={selectedBlock?.actions?.filter((action) => action.progressStatus === COMPLETE) ?? []}
          isOpen={isOpenSetUncompletedActionsModal}
          onClose={onCloseSetUncompletedActionsModal}
          onDeleteBlock={({ id }) => deleteBlock.mutate({ blockId: id })}
          onCompleteBlockActions={onCompleteBlockActions}
          onRemoveBlockActions={onRemoveBlockActions}
          onCompleteBlock={onCompleteBlock}
        />
      )}
      {isOpenDeleteBlockModal && (
        <DeleteBlockModal isOpen={isOpenDeleteBlockModal} onClose={onCloseDeleteBlockModal} onSubmit={onDeleteBlock} />
      )}
      {isOpenConfirmDeleteBlockModal && (
        <ConfirmDeleteBlockModal
          isOpen={isOpenConfirmDeleteBlockModal}
          onClose={onCloseConfirmDeleteBlockModal}
          onSubmit={onDeleteBlock}
        />
      )}
      {isOpenCreateBlockModal && data && (
        <CreateBlockModal
          isOpen={isOpenCreateBlockModal}
          onClose={onCloseAndResetCreateBlockModal}
          scheduledDate={selectedDate}
          actionId={tempBlockActionId ?? ''}
          categoryId={tempCategoryId ?? ''}
          projectId={tempProjectId ?? ''}
        />
      )}
      {isScheduleEventModalOpen && (
        <ScheduleEventModal
          isOpen={isScheduleEventModalOpen}
          onSave={handleDailyActionDropped}
          onClose={onCloseScheduleEventModal}
        />
      )}

      {showCanceledModal && selectedCategoryForBulk && (
        <DeleteBulkActionsModal
          isOpen={showCanceledModal}
          onClose={() => setShowCanceledModal(false)}
          actions={selectedCategoryForBulk.incompleteActions}
          mode={modeValues.canceled}
          recurringDurationRange={captureDurationRange ?? undefined}
        />
      )}
      {showDeleteModal && selectedCategoryForBulk && (
        <DeleteBulkActionsModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          actions={selectedCategoryForBulk.incompleteActions}
          mode={modeValues.delete}
          recurringDurationRange={captureDurationRange ?? undefined}
        />
      )}

      {isEditRecurringEvent && (
        <RecurringEventModal
          isOpen={isEditRecurringEvent}
          onCancel={handleCloseEditRecurringAction}
          onConfirm={editRecurringEvents}
          event={eventToUnplan}
          isSameDay={true}
          mode="edit"
        />
      )}

      {isDeleteRecurringEvents && (
        <RecurringEventModal
          isOpen={isDeleteRecurringEvents}
          onCancel={handleCloseDeleteDuplicatedAction}
          onConfirm={deleteRecurringEvents}
          event={eventToUnplan}
          isSameDay={true}
        />
      )}

      <RecurringInBlockModal isOpen={isOpenRecurringInBlockModal} onClose={closeRecurringInBlockModal} />
    </DashboardLayout>
  );
}

export default Schedule;
